import { AmlKycRevalidationStateEnum, CustomerBlockingReasonsEnum } from '.spec/self-care/tsTypes';

import type { HintedString, Maybe } from '@skippay/core/utils/types';

type BlockingReason = HintedString<CustomerBlockingReasonsEnum>;

export interface ComplianceInput {
	kycIsOutdated?: boolean;
	amlLegalReviewRequested?: boolean;
	amlLegalReviewOutdated?: boolean;
	amlKycRevalidationState?: Maybe<AmlKycRevalidationStateEnum>;
	pepAssetsOriginQuestionnaireRequired?: boolean;
	pepBankIdRequired?: boolean;
	customerBlockingReasons?: BlockingReason[];
}

export enum ActionFlag {
	RENEW_IDENTITY_VERIFICATION = 'renew_identity_verification',
	COMPLETE_AML_REVIEW = 'complete_aml_review',
	COMPLETE_PEP_QUESTIONNAIRE = 'complete_pep_questionnaire',
	ACCOUNT_BLOCKED = 'account_blocked',
}

export enum BlockedCategory {
	OVERDUE = 'blocked_overdue',
	PEP = 'blocked_pep',
	AML = 'blocked_aml',
	OTHER = 'blocked_other',
}

export const ACTION_FLAGS: readonly ActionFlag[] = [
	ActionFlag.RENEW_IDENTITY_VERIFICATION,
	ActionFlag.COMPLETE_AML_REVIEW,
	ActionFlag.COMPLETE_PEP_QUESTIONNAIRE,
	ActionFlag.ACCOUNT_BLOCKED,
];

export const BLOCKED_CATEGORIES: readonly BlockedCategory[] = [
	BlockedCategory.OVERDUE,
	BlockedCategory.PEP,
	BlockedCategory.AML,
	BlockedCategory.OTHER,
];

const BLOCKED_CATEGORY_BY_REASON = new Map<BlockingReason, BlockedCategory>([
	[CustomerBlockingReasonsEnum.BILLING_OVERDUE, BlockedCategory.OVERDUE],
	[CustomerBlockingReasonsEnum.DEFERRED_PAYMENT_OVERDUE, BlockedCategory.OVERDUE],
	[CustomerBlockingReasonsEnum.PAY_IN_THREE_INSTALLMENT_OVERDUE, BlockedCategory.OVERDUE],
	[CustomerBlockingReasonsEnum.AML_ASSETS_ORIGIN_QUESTIONNAIRE_UNMET, BlockedCategory.PEP],
	[CustomerBlockingReasonsEnum.AML_LEGAL_REVIEW_EXPIRED, BlockedCategory.AML],
	[CustomerBlockingReasonsEnum.INSUFFICIENT_KYC_IDENTIFY_AML, BlockedCategory.AML],
]);

export function toActionFlags(input: ComplianceInput): ActionFlag[] {
	const flags = new Set<ActionFlag>();

	if (
		input.kycIsOutdated ||
		input.amlKycRevalidationState === AmlKycRevalidationStateEnum.BLOCKED
	) {
		flags.add(ActionFlag.RENEW_IDENTITY_VERIFICATION);
	}
	if (input.amlLegalReviewRequested || input.amlLegalReviewOutdated) {
		flags.add(ActionFlag.COMPLETE_AML_REVIEW);
	}
	if (input.pepAssetsOriginQuestionnaireRequired || input.pepBankIdRequired) {
		flags.add(ActionFlag.COMPLETE_PEP_QUESTIONNAIRE);
	}
	if (input.customerBlockingReasons && input.customerBlockingReasons.length > 0) {
		flags.add(ActionFlag.ACCOUNT_BLOCKED);
	}

	return Array.from(flags);
}

export function toBlockedCategories(customerBlockingReasons?: BlockingReason[]): BlockedCategory[] {
	if (!customerBlockingReasons || customerBlockingReasons.length === 0) return [];

	return Array.from(
		new Set(
			customerBlockingReasons.map(
				(reason) => BLOCKED_CATEGORY_BY_REASON.get(reason) ?? BlockedCategory.OTHER
			)
		)
	);
}
